import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const ROLE_LEVELS = {
  super_admin: 100, admin: 80, producer: 60, account_manager: 60,
  service_rep: 40, read_only: 20, restricted: 10,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [config, setConfig] = useState({});
  const [setupRequired, setSetupRequired] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/config");
      setConfig(data.items || {});
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        loadConfig();
      } catch {
        setUser(false);
        try {
          const { data } = await api.get("/auth/setup-status");
          setSetupRequired(!!data.setup_required);
        } catch { /* ignore */ }
      }
    })();
  }, [loadConfig]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      loadConfig();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const setupFirstAdmin = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/setup", { email, password, name });
      setSetupRequired(false);
      setUser(data);
      loadConfig();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; }
    catch { setUser(false); return null; }
  }, []);

  const level = user ? (ROLE_LEVELS[user.role] || 0) : 0;
  const can = {
    write: level >= 40,
    archive: level >= 60,
    admin: level >= 80,
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, setupRequired, setupFirstAdmin, config, loadConfig, level, can, ROLE_LEVELS }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
